import { startOfToday } from "date-fns";
import { useMemo } from "react";
import { defineMessages, useIntl } from "react-intl";

import { useActiveOrganization } from "common/account/active_organization";
import { Badge } from "common/core/badge";
import { Hr } from "common/core/horizontal_rule";
import LoadingIndicator from "common/core/loading_indicator";
import { Heading, Paragraph } from "common/core/typography";
import { ReportingIntervalEnum } from "graphql_globals";
import { useQuery } from "util/graphql";

import { useDateConstraint } from "../filter_dropdown/common";
import { CompletedDetailsSection } from "./completed_details_section";
import Styles from "./index.module.scss";
import ReportsDetails from "./index.query.graphql";
import { SentCompletedSection } from "./sent_completed_section";
import { isNotarizeCountScalarHashArray } from "./shared/utils";

const MESSAGES = defineMessages({
  title: {
    id: "57e1be81-29a1-49e1-80e7-75e7f6cdb1e1",
    defaultMessage: "{orgName} analytics",
  },
  titleTag: {
    id: "31d8d26a-9eed-41a5-b47e-22695ced9c97",
    defaultMessage: "Beta",
  },
  subtitle: {
    id: "9c2eb11b-2765-418a-acbd-ec466477a8d9",
    defaultMessage: "Reports give you an in-depth look at trends in your data.",
  },
  question: {
    id: "50815351-68b7-4800-8ab1-5cd4e3ca79fd",
    defaultMessage: "Looking for something?",
  },
  viewingLast30Days: {
    id: "fb344846-a509-424d-a9f0-8af0e331ca08",
    defaultMessage: "Viewing {startDate} - {endDate} | Last 30 days",
  },
});

export function Reporting() {
  const [activeOrganizationId] = useActiveOrganization();
  const intl = useIntl();

  const today = startOfToday();

  const formatDate = (date: Date) => date.toISOString().slice(0, 10);

  const { startDate, endDate } = useDateConstraint(
    useMemo(
      () => ({
        startDate: formatDate(new Date(today.getTime() - 30 * 24 * 60 * 60 * 1000)),
        endDate: formatDate(today),
      }),
      [today],
    ),
  );

  const { startDate: priorPeriodStartDate, endDate: priorPeriodEndDate } = useDateConstraint(
    useMemo(
      () => ({
        startDate: formatDate(new Date(new Date().setDate(today.getDate() - 60))),
        endDate: formatDate(new Date(new Date().setDate(today.getDate() - 30))),
      }),
      [today],
    ),
  );

  const { startDate: sixMonthsStartDate, endDate: sixMonthsEndDate } = useDateConstraint(
    useMemo(
      () => ({
        startDate: formatDate(new Date(new Date().setDate(today.getDate() - 180))),
        endDate: formatDate(today),
      }),
      [today],
    ),
  );

  const viewingLast30Days = intl.formatMessage(MESSAGES.viewingLast30Days, {
    startDate: startDate
      ? new Date(startDate).toLocaleDateString("default", {
          month: "short",
          year: "numeric",
        })
      : "",
    endDate: endDate
      ? new Date(endDate).toLocaleDateString("default", {
          month: "short",
          year: "numeric",
        })
      : "",
  });

  // can also change interval to "daily" or "weekly" if needed
  // right now reports default to monthly intervals except for sentCompleted
  const queryVariables = useMemo(
    () => ({
      organizationId: activeOrganizationId!,
      sixMonthsStartDate: sixMonthsStartDate!,
      sixMonthsEndDate: sixMonthsEndDate!,
      startDate: startDate!,
      endDate: endDate!,
      priorPeriodStartDate: priorPeriodStartDate!,
      priorPeriodEndDate: priorPeriodEndDate!,
      sentCompletedInterval: ReportingIntervalEnum.MONTH,
    }),
    [
      activeOrganizationId,
      startDate,
      endDate,
      priorPeriodStartDate,
      priorPeriodEndDate,
      sixMonthsStartDate,
      sixMonthsEndDate,
    ],
  );

  const { data, loading } = useQuery(ReportsDetails, {
    variables: queryVariables,
    fetchPolicy: "cache-first",
    nextFetchPolicy: "cache-first",
    skip: !activeOrganizationId,
  });

  if (loading) {
    return <LoadingIndicator />;
  }

  if (data?.organization?.__typename !== "Organization") {
    throw new Error(`Expected Organization, got ${data?.organization?.__typename}`);
  }

  const { organization, viewer } = data;
  const { sentCompleted, completedDetails, priorPeriodCompleted, currentPeriodCompleted } = viewer;

  if (!isNotarizeCountScalarHashArray(completedDetails)) {
    throw new Error("Expected completedDetails to be an array of NotarizeCountScalarHash");
  }
  if (!isNotarizeCountScalarHashArray(priorPeriodCompleted)) {
    throw new Error("Expected priorPeriodCompleted to be an array of NotarizeCountScalarHash");
  }
  if (!isNotarizeCountScalarHashArray(currentPeriodCompleted)) {
    throw new Error("Expected currentPeriodCompleted to be an array of NotarizeCountScalarHash");
  }

  return (
    <div className={Styles.page}>
      <div className={Styles.pageContent}>
        <Heading level="h1" textStyle="headingThree" className={Styles.title}>
          {intl.formatMessage(MESSAGES.title, { orgName: organization.name })}
          <Badge kind="new" className={Styles.badge}>
            {intl.formatMessage(MESSAGES.titleTag)}
          </Badge>
        </Heading>
        <Paragraph textColor="subtle" className={Styles.description}>
          <span>{intl.formatMessage(MESSAGES.subtitle)}</span>
          <span data-pendo-id="reporting-feedback-request">
            {intl.formatMessage(MESSAGES.question)}
          </span>
        </Paragraph>
        <Hr />
        <div className={Styles.section}>
          <SentCompletedSection
            chartData={sentCompleted}
            sixMonthsStartDate={sixMonthsStartDate}
            sixMonthsEndDate={sixMonthsEndDate}
          />
          <CompletedDetailsSection
            completeDistributionReport={completedDetails}
            currentPeriodReport={currentPeriodCompleted}
            priorPeriodReport={priorPeriodCompleted}
            last30Days={viewingLast30Days}
            today={today}
          />
        </div>
      </div>
    </div>
  );
}
